import * as React from "react";
import { Camera, Check, Languages, Loader2, LogOut, Monitor, Moon, Sun, Bell } from "lucide-react";
import { apiErrorMessage } from "../lib/apiErrorCopy";
import { Button, cn, useAppearance, type ColorMode } from "@eduviaos/ui-kit";
import { Reveal, SectionCard, SectionHeader } from "../components/Section";
import { JOY_TINT } from "../lib/joy";
import { useMarkNotificationRead, useNotifications } from "../lib/learning/hooks";
import { SCHEDULE, UPDATES } from "../lib/mockData";
import { verticalLabel, type Institution } from "../lib/institution";
import type { LearnLocale } from "../lib/i18n/learn";
import { usePortalLocale } from "../lib/i18n/usePortalLocale";
import { intlLocale } from "../lib/i18n/intl";
import { commitAvatar, mintAvatarUploadUrl, putFileToSignedUrl } from "../lib/profile/api";
import { profileRoleLabel } from "../lib/profileRoleLabel";
import { useSession } from "../lib/sessionContext";
import { useZiggyVariant } from "../lib/ziggyContext";
import { useSetZiggyVariant } from "../lib/useZiggyPersistence";
import { ZIGGY_VARIANTS, ZIGGY_VARIANT_META, ziggySrc, type ZiggyAge } from "../lib/ziggy";

/** Full-day timetable (student "Schedule" tab). */
export function SchedulePanel() {
  return (
    <Reveal>
      <SectionHeader title="Today's Schedule" />
      <SectionCard className="divide-border/60 divide-y p-0">
        {SCHEDULE.map((item) => (
          <div key={item.period} className="flex items-center gap-3 px-4 py-3">
            <span
              className={cn(
                "grid size-11 shrink-0 place-items-center rounded-xl",
                JOY_TINT[item.color],
              )}
            >
              <item.icon className="size-5" />
            </span>
            <div className="flex-1">
              <p className="font-semibold">{item.subject}</p>
              <p className="text-muted-foreground text-xs">Period {item.period}</p>
            </div>
            <span className="text-muted-foreground text-sm font-medium tabular-nums">
              {item.time}
            </span>
          </div>
        ))}
      </SectionCard>
    </Reveal>
  );
}

/** Announcements / notices tab (shared). Study-centre learners read real API data. */
export function NoticesPanel({ useLearningApi = false }: { useLearningApi?: boolean }) {
  const notificationsQuery = useNotifications(useLearningApi);
  const markRead = useMarkNotificationRead();
  const { strings, locale } = usePortalLocale(useLearningApi);
  const items = notificationsQuery.data?.items ?? [];
  const unreadCount = notificationsQuery.data?.unread_count ?? 0;
  const [markAllFailed, setMarkAllFailed] = React.useState(false);
  const [markingAll, setMarkingAll] = React.useState(false);

  // Reading a notice is the only way the bell's dot can ever clear, so the
  // list has to be able to mark items read — before this it was a read-only
  // wall of text and the dot stayed lit forever.
  async function markAllRead() {
    const unread = items.filter((note) => !note.read_at);
    if (unread.length === 0) return;
    setMarkAllFailed(false);
    setMarkingAll(true);
    const results = await Promise.allSettled(unread.map((note) => markRead.mutateAsync(note.id)));
    setMarkingAll(false);
    if (results.some((r) => r.status === "rejected")) setMarkAllFailed(true);
  }

  if (useLearningApi) {
    return (
      <Reveal>
        <SectionHeader
          title={
            unreadCount > 0
              ? `${strings.noticesTitle} · ${strings.noticesUnread(unreadCount)}`
              : strings.noticesTitle
          }
          action={
            unreadCount > 0
              ? markingAll
                ? strings.noticesMarkingAllRead
                : strings.noticesMarkAllRead
              : undefined
          }
          onAction={unreadCount > 0 && !markingAll ? () => void markAllRead() : undefined}
        />
        {notificationsQuery.isLoading ? (
          <SectionCard className="text-muted-foreground flex items-center justify-center gap-2 py-10 text-sm">
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            {strings.noticesLoading}
          </SectionCard>
        ) : notificationsQuery.isError ? (
          <SectionCard className="space-y-2 px-4 py-6 text-center">
            <p className="font-semibold">{strings.noticesLoadFailedTitle}</p>
            <p className="text-muted-foreground text-sm">{strings.noticesLoadFailedBody}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => notificationsQuery.refetch()}
            >
              {strings.homeRetry}
            </Button>
          </SectionCard>
        ) : items.length === 0 ? (
          <SectionCard className="text-muted-foreground px-4 py-8 text-center text-sm">
            {strings.noticesEmpty}
          </SectionCard>
        ) : (
          <SectionCard className="divide-border/60 divide-y p-0">
            <ul aria-label={strings.noticesListAria}>
              {items.map((note) => {
                const unread = !note.read_at;
                return (
                  <li key={note.id} className="border-border/60 border-t first:border-0">
                    <button
                      type="button"
                      disabled={!unread}
                      aria-label={unread ? strings.noticesOpen(note.title) : undefined}
                      onClick={() => unread && markRead.mutate(note.id)}
                      className={cn(
                        "flex w-full items-start gap-3 px-4 py-3 text-left transition-colors duration-150 ease-out",
                        unread ? "bg-primary/[0.04] hover:bg-primary/8" : "cursor-default",
                      )}
                    >
                      <span
                        className={cn(
                          "relative grid size-10 shrink-0 place-items-center rounded-xl",
                          unread ? "bg-primary/12 text-primary" : "bg-muted text-muted-foreground",
                        )}
                      >
                        <Bell className="size-5" aria-hidden="true" />
                        {unread && (
                          <span className="bg-celebrate ring-card absolute -right-0.5 -top-0.5 size-2.5 rounded-full ring-2" />
                        )}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className={cn("font-semibold", !unread && "text-muted-foreground")}>
                          {note.title}
                        </p>
                        <p className="text-muted-foreground text-sm">{note.body}</p>
                        <p className="text-muted-foreground mt-0.5 text-xs">
                          {formatNoticeWhen(note.created_at, locale)}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </SectionCard>
        )}
        {markAllFailed && (
          <p role="alert" className="text-destructive mt-2 text-center text-xs font-medium">
            {strings.noticesMarkAllFailed}
          </p>
        )}
      </Reveal>
    );
  }

  return (
    <Reveal>
      <SectionHeader title="Notices" />
      <SectionCard className="divide-border/60 divide-y p-0">
        {UPDATES.map((u) => (
          <div key={u.title} className="flex items-start gap-3 px-4 py-3">
            <span
              className={cn(
                "grid size-10 shrink-0 place-items-center rounded-xl",
                JOY_TINT[u.color],
              )}
            >
              <u.icon className="size-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-semibold">{u.title}</p>
              <p className="text-muted-foreground text-sm">{u.detail}</p>
              <p className="text-muted-foreground mt-0.5 text-xs">{u.when}</p>
            </div>
          </div>
        ))}
      </SectionCard>
    </Reveal>
  );
}

function formatNoticeWhen(iso: string, locale: LearnLocale): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(intlLocale(locale), {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const MODES: {
  mode: ColorMode;
  labelKey: "themeLight" | "themeDark" | "themeSystem";
  icon: typeof Sun;
}[] = [
  { mode: "light", labelKey: "themeLight", icon: Sun },
  { mode: "dark", labelKey: "themeDark", icon: Moon },
  { mode: "system", labelKey: "themeSystem", icon: Monitor },
];

/** Profile + appearance controls (shared). */
export function ProfilePanel({
  institution,
  ziggyAge = "teen",
}: {
  institution: Institution;
  ziggyAge?: ZiggyAge;
}) {
  const { user, signOut, refreshProfile } = useSession();
  const { mode, set } = useAppearance();
  const { variant: ziggyVariant } = useZiggyVariant();
  const setZiggyVariant = useSetZiggyVariant();
  const isStudyCentre = institution.vertical === "study_centre";
  const { locale, setLocale, strings, languageSyncStatus } = usePortalLocale(isStudyCentre);
  const roleLabel = profileRoleLabel(user?.role, isStudyCentre, strings);
  const [avatarBusy, setAvatarBusy] = React.useState(false);
  const [avatarError, setAvatarError] = React.useState<string | null>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);
  const initials = (user?.name ?? "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("");

  async function onAvatarPick(file: File) {
    setAvatarError(null);
    setAvatarBusy(true);
    try {
      const signed = await mintAvatarUploadUrl({
        content_type: file.type || "image/png",
        file_size: file.size,
      });
      await putFileToSignedUrl(signed, file);
      await commitAvatar(signed.public_url);
      await refreshProfile();
    } catch (err) {
      setAvatarError(apiErrorMessage(err, locale, strings, strings.avatarUpdateFailed));
    } finally {
      setAvatarBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <Reveal>
        <SectionCard className="flex items-center gap-4">
          <button
            type="button"
            aria-label={strings.changeProfilePhoto}
            disabled={avatarBusy}
            onClick={() => fileRef.current?.click()}
            className="relative shrink-0"
          >
            {user?.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt=""
                className="size-16 rounded-full object-cover shadow-[var(--shadow-sm)]"
              />
            ) : (
              <span className="from-primary grid size-16 place-items-center rounded-full bg-gradient-to-br to-[color-mix(in_oklch,var(--color-primary)_55%,oklch(0.66_0.2_340))] text-2xl font-semibold text-white shadow-[var(--shadow-sm)]">
                {initials}
              </span>
            )}
            <span className="bg-background border-border/60 absolute -bottom-0.5 -right-0.5 grid size-7 place-items-center rounded-full border shadow-sm">
              {avatarBusy ? (
                <Loader2 className="text-muted-foreground size-3.5 animate-spin" />
              ) : (
                <Camera className="text-muted-foreground size-3.5" />
              )}
            </span>
          </button>
          {/* The styled button above is the labelled proxy for this input.
              Left in the tab order it gave keyboard users a second stop that
              announced as the browser's own English "Choose File". */}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onAvatarPick(file);
              e.target.value = "";
            }}
          />
          <div className="min-w-0">
            {/* The learner's name is the Profile tab's page title — without it
                the tab started at h2 and had no top-level heading at all. */}
            <h1 className="font-display truncate text-lg font-semibold">{user?.name}</h1>
            <p className="text-muted-foreground text-sm">{roleLabel}</p>
            <p className="text-muted-foreground text-xs">
              {institution.shortName} · {verticalLabel(institution.vertical)}
            </p>
            {avatarError && (
              <p role="alert" className="text-destructive mt-1 text-xs">
                {avatarError}
              </p>
            )}
          </div>
        </SectionCard>
      </Reveal>

      <Reveal index={1}>
        <SectionHeader title={strings.appearance} />
        <SectionCard>
          <div
            role="radiogroup"
            aria-label={strings.themeGroupAria}
            className="bg-muted grid grid-cols-3 gap-1 rounded-xl p-1"
          >
            {MODES.map((m) => {
              const active = mode === m.mode;
              return (
                <button
                  key={m.mode}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => set({ mode: m.mode })}
                  className={cn(
                    "flex items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-medium transition-colors duration-150",
                    active
                      ? "bg-card text-foreground shadow-[var(--shadow-sm)]"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <m.icon className="size-4" />
                  {strings[m.labelKey]}
                </button>
              );
            })}
          </div>
          <p className="text-muted-foreground mt-3 text-xs">{strings.appearanceHint}</p>
        </SectionCard>
      </Reveal>

      {isStudyCentre && (
        <Reveal index={2}>
          <SectionHeader title={strings.appLanguage} />
          <SectionCard>
            <div
              role="radiogroup"
              aria-label={strings.appLanguageGroupAria}
              className="bg-muted grid grid-cols-2 gap-1 rounded-xl p-1"
            >
              {(
                [
                  { code: "en" as LearnLocale, label: "English" },
                  { code: "hi" as LearnLocale, label: "हिंदी" },
                ] as const
              ).map((opt) => {
                const active = locale === opt.code;
                return (
                  <button
                    key={opt.code}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setLocale(opt.code)}
                    className={cn(
                      "flex items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-medium transition-colors duration-150",
                      active
                        ? "bg-card text-foreground shadow-[var(--shadow-sm)]"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <Languages className="size-4" />
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <p className="text-muted-foreground mt-3 text-xs">{strings.appLanguageHint}</p>
            {languageSyncStatus && (
              <p role="status" className="text-muted-foreground mt-2 text-xs">
                {languageSyncStatus === "device_only"
                  ? strings.languageDeviceOnly
                  : strings.languageSaveFailed}
              </p>
            )}
          </SectionCard>
        </Reveal>
      )}

      <Reveal index={isStudyCentre ? 3 : 2}>
        <SectionHeader title={strings.ziggySectionTitle} />
        <SectionCard>
          <div
            role="radiogroup"
            aria-label={strings.ziggyLookGroupAria}
            className="grid grid-cols-2 gap-3"
          >
            {ZIGGY_VARIANTS.map((v) => {
              const meta = ZIGGY_VARIANT_META[v];
              const active = ziggyVariant === v;
              return (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  aria-label={strings.ziggyVariantAria(meta.label)}
                  onClick={() => setZiggyVariant(v)}
                  className={cn(
                    "relative flex items-center gap-3 rounded-2xl border-2 p-3 text-left transition-all duration-150 ease-out active:scale-[0.98]",
                    active
                      ? "border-primary bg-primary/5"
                      : "border-border/60 bg-muted/30 hover:border-primary/40",
                  )}
                >
                  <img
                    src={ziggySrc(v, ziggyAge, "happy")}
                    alt=""
                    aria-hidden
                    loading="lazy"
                    draggable={false}
                    className="h-16 w-auto shrink-0 object-contain drop-shadow-md"
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold">{meta.label}</span>
                    {/* Two lines rather than one truncated line: at 390px the
                        blurb was clipped mid-word on every variant. */}
                    <span className="text-muted-foreground line-clamp-2 block text-xs leading-snug">
                      {v === "pink" ? strings.ziggyBlurbPink : strings.ziggyBlurbBlue}
                    </span>
                  </span>
                  {active && (
                    <span className="bg-primary text-primary-foreground absolute right-2 top-2 grid size-5 place-items-center rounded-full">
                      <Check className="size-3.5" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <p className="text-muted-foreground mt-3 text-xs">{strings.ziggySectionHint}</p>
        </SectionCard>
      </Reveal>

      <Reveal index={isStudyCentre ? 4 : 3}>
        <Button
          variant="outline"
          onClick={signOut}
          className="text-destructive border-destructive/30 hover:bg-destructive/5 h-12 w-full gap-2 rounded-xl"
        >
          <LogOut className="size-4" /> {strings.signOut}
        </Button>
      </Reveal>
    </div>
  );
}
